import { useState, useCallback, useEffect } from 'react';
import {
  Form,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Select,
  Alert,
  Box,
  Toggle,
  SelectProps,
} from '@cloudscape-design/components';
import useUserProfile from 'src/hooks/useUserProfile';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import RequiredFieldLabel from '../RequiredFieldLabel';
import {
  COUNTRY_OPTIONS,
  REGION_OPTIONS,
  getCountriesByRegion,
} from 'src/contstant/commonLabels';
import { CreateLocationCommandInput } from '@amzn/gbsa-helios-typescript-client';
import { useMutation } from '@tanstack/react-query';
import { locationService } from 'src/data/api/helios-service-ts/locationService';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Persona } from 'src/contstant/Persona';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
interface LocationDetailsFormProps {
  onViewDismiss: () => void;
}
type LocationParams = Pick<
  CreateLocationCommandInput,
  'businessUnitId' | 'locationCode' | 'isEnabledForBU' | 'region' | 'country'
>;

const locationFormSchema = z.object({
  businessUnitId: z.string({
    error: 'Business Unit is required',
  }),
  locationCode: z.string({
    error: 'Location Code is required',
  }),
  isEnabledForBU: z.boolean(),
  region: z.string({
    error: 'Region is required',
  }),
  country: z
    .string({
      error: 'Country is required',
    })
    .min(1, {
      message: 'Country is required',
    }),
}) satisfies z.ZodType<LocationParams>;
export function LocationDetailsFormCreate({ onViewDismiss }: LocationDetailsFormProps) {
  const handleCloseLocationForm = () => {
    onViewDismiss();
  };
  const [availableCountries, setAvailableCountries] =
    useState<SelectProps.Option[]>(COUNTRY_OPTIONS);
  const { getUserId, getPersona, getUserBusinessUnitId, getUserProgramId, getDivisions } =
    useUserProfile();

  const { userPermission, checkUserPermissions } = useUsers();
  const form = useForm({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      businessUnitId: getUserBusinessUnitId(),
      isEnabledForBU: true,
    },
  });
  const isEditLocation = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionConfig.LOCATION.EDIT_LOCATION,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions, getPersona]);

  const {
    mutate: createLocationMutation,
    status: createLocationStatus,
    error: createLocationError,
  } = useMutation({
    mutationFn: (data: CreateLocationCommandInput) => {
      return locationService.createLocation({
        ...data,
        businessUnitId: getUserBusinessUnitId(),
      });
    },
    onSuccess() {
      handleCloseLocationForm();
    },
  });
  useEffect(() => {
    if (getPersona() != Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [permissionAccessConfig.LOCATION.EDIT_LOCATION],
        divisions: getDivisions(),
      });
    }
  }, [
    getPersona(),
    getUserProgramId(),
    getUserBusinessUnitId(),
    getDivisions(),
    getUserId(),
  ]);
  if (!isEditLocation()) {
    return (
      <Box margin={{ top: 'xl' }}>
        <Alert type="info">You are not authorized to create a location.</Alert>
      </Box>
    );
  }

  return (
    <form
      onSubmit={form.handleSubmit((data) =>
        createLocationMutation({ ...data, createdBy: getUserId() }),
      )}
      id="location-create-form"
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="cancel-button"
              formAction="none"
              variant="link"
              onClick={handleCloseLocationForm}
              disabled={createLocationStatus === 'pending'}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              form="location-create-form"
              formAction="submit"
              loading={createLocationStatus === 'pending'}
              disabled={createLocationStatus === 'pending'}
            >
              Save
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <Controller
            name="locationCode"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="locationCode-field"
                label={<RequiredFieldLabel label="Location" />}
                errorText={form.formState.errors.locationCode?.message}
              >
                <Input
                  data-testid="locationCode-input"
                  {...field}
                  value={field.value}
                  onChange={({ detail }) => field.onChange(detail.value)}
                />
              </FormField>
            )}
          />
          <Controller
            name="businessUnitId"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="businessUnitId-field"
                label={<RequiredFieldLabel label="Business Unit" />}
                errorText={form.formState.errors.businessUnitId?.message}
              >
                <Input data-testid="businessUnit-input" value={field.value} readOnly />
              </FormField>
            )}
          />
          <Controller
            name="region"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="region-field"
                label={<RequiredFieldLabel label="Region" />}
                errorText={form.formState.errors.region?.message}
              >
                <Select
                  data-testid="region-select"
                  options={REGION_OPTIONS}
                  selectedOption={
                    REGION_OPTIONS.find((option) => option.value === field.value) || null
                  }
                  placeholder="Choose a Region"
                  filteringType="auto"
                  onChange={({ detail }) => {
                    const selectedRegion = detail.selectedOption.value || '';
                    setAvailableCountries(getCountriesByRegion(selectedRegion));
                    field.onChange(selectedRegion);
                    form.setValue('country', '');
                  }}
                />
              </FormField>
            )}
          />
          <Controller
            name="country"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="country-field"
                label={<RequiredFieldLabel label="Country" />}
                errorText={form.formState.errors.country?.message}
              >
                <Select
                  data-testid="country-select"
                  options={availableCountries}
                  selectedOption={
                    availableCountries.find((option) => option.value === field.value) ||
                    null
                  }
                  placeholder="Choose a Country"
                  filteringType="auto"
                  onChange={({ detail }) =>
                    field.onChange(detail.selectedOption.value || '')
                  }
                />
              </FormField>
            )}
          />
          <Controller
            name="isEnabledForBU"
            control={form.control}
            render={({ field }) => (
              <FormField
                label="Status"
                constraintText="Enable or disable the location using this switch."
                errorText={form.formState.errors.isEnabledForBU?.message}
              >
                <Toggle
                  data-testid="enabled-toggle"
                  checked={field.value}
                  onChange={({ detail }) => field.onChange(detail.checked)}
                >
                  {field.value ? 'Active' : 'Inactive'}
                </Toggle>
              </FormField>
            )}
          />
        </SpaceBetween>
      </Form>

      {createLocationStatus === 'error' ? (
        <Box margin={{ top: 'xl' }}>
          <Alert type="error">
            {createLocationError?.message ??
              'An error occurred while creating the location'}
          </Alert>
        </Box>
      ) : null}
    </form>
  );
}
